import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { notificationAPI } from '../../services/api';
import { marketingPalette } from '../../content/marketing';

const palette = marketingPalette;

const formatNotificationTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

function NotificationBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await notificationAPI.list({ limit: 8 });
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unreadCount || 0);
    } catch (error) {
      // The bell should never prevent the rest of the navigation from working.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Unable to load notifications', error);
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications({ quiet: true });
    const refresh = () => {
      if (document.visibilityState === 'visible') loadNotifications({ quiet: true });
    };
    const timer = window.setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('virtual-medic:notifications-changed', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('virtual-medic:notifications-changed', refresh);
    };
  }, [loadNotifications]);

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleNotification = async (notification) => {
    setAnchorEl(null);
    if (!notification.readAt) {
      setNotifications((current) => current.map((item) => (
        item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item
      )));
      setUnreadCount((current) => Math.max(current - 1, 0));
      try {
        await notificationAPI.markRead(notification.id);
        window.dispatchEvent(new Event('virtual-medic:notifications-changed'));
      } catch (error) {
        loadNotifications({ quiet: true });
      }
    }
    if (notification.link) navigate(notification.link);
  };

  const markAllRead = async () => {
    setUnreadCount(0);
    setNotifications((current) => current.map((item) => ({
      ...item,
      readAt: item.readAt || new Date().toISOString(),
    })));
    try {
      await notificationAPI.markAllRead();
      window.dispatchEvent(new Event('virtual-medic:notifications-changed'));
    } catch (error) {
      loadNotifications({ quiet: true });
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
          onClick={openMenu}
          sx={{
            width: 40,
            height: 40,
            color: palette.ink,
            border: `1px solid ${alpha(palette.ink, 0.08)}`,
            bgcolor: alpha('#ffffff', 0.72),
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsNoneRoundedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 'min(390px, calc(100vw - 24px))',
            maxHeight: 'min(620px, calc(100vh - 100px))',
            borderRadius: '20px',
            border: `1px solid ${alpha(palette.ink, 0.08)}`,
            boxShadow: '0 20px 56px rgba(18, 61, 103, 0.16)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
          <Box>
            <Typography sx={{ fontWeight: 900, color: palette.ink }}>Notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount ? `${unreadCount} unread` : 'You are up to date'}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead} sx={{ textTransform: 'none', fontWeight: 800 }}>
              Mark all read
            </Button>
          )}
        </Stack>
        <Divider />
        {loading ? (
          <Box sx={{ py: 5, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        ) : notifications.length ? (
          <List disablePadding>
            {notifications.map((notification) => (
              <ListItemButton
                key={notification.id}
                onClick={() => handleNotification(notification)}
                sx={{
                  alignItems: 'flex-start',
                  px: 2,
                  py: 1.5,
                  bgcolor: notification.readAt ? 'transparent' : alpha(palette.heroBlue, 0.09),
                  borderBottom: `1px solid ${alpha(palette.ink, 0.06)}`,
                }}
              >
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {!notification.readAt && (
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: palette.heroBlueDeep, flexShrink: 0 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: notification.readAt ? 700 : 900, color: palette.ink }}>
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
                      {formatNotificationTime(notification.createdAt)}
                    </Typography>
                  </Stack>
                  {notification.message && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
                      {notification.message}
                    </Typography>
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800, color: palette.ink }}>Nothing new</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Feedback, assignments, and announcements will appear here.
            </Typography>
          </Box>
        )}
        <Button
          fullWidth
          onClick={() => {
            setAnchorEl(null);
            navigate('/notifications');
          }}
          sx={{ py: 1.4, textTransform: 'none', fontWeight: 900 }}
        >
          View all notifications
        </Button>
      </Menu>
    </>
  );
}

export default NotificationBell;
